import React, { useState, useCallback } from 'react';
import { unifiedApi } from '../../api/unifiedApi';

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 验证密码格式（4位字母数字组合）
  const validatePassword = (pwd: string): boolean => {
    return /^[a-zA-Z0-9]{4}$/.test(pwd);
  };

  // 处理密码输入
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4);
    setPassword(value);
    setError(null);
  };

  // 处理确认密码输入
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4);
    setConfirmPassword(value);
    setError(null);
  };

  // 检查密码是否匹配
  const passwordsMatch = password === confirmPassword && password.length === 4;

  const handleSubmit = useCallback(async () => {
    // 验证输入
    if (!validatePassword(password)) {
      setError('请输入4位字母数字密码');
      return;
    }
    if (!validatePassword(confirmPassword)) {
      setError('请确认密码');
      return;
    }
    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await unifiedApi.setLockPassword(password);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('[ResetPasswordModal] 设置密码失败:', err);
      setError('设置密码失败，请重试');
    } finally {
      setIsLoading(false);
    }
  }, [password, confirmPassword, onSuccess, onClose]);

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // 点击遮罩层关闭
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 border border-gray-200 dark:border-gray-700 relative">
        {/* 关闭按钮 */}
        <button
          onClick={handleClose}
          disabled={isLoading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* 标题 */}
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              设置新的解锁密码
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              请输入两次相同的4位密码
            </p>
          </div>
        </div>

        {/* 说明内容 */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-6">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            设置解锁密码后，您可以给笔记上锁保护隐私。
          </p>
        </div>

        {/* 密码输入区域 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            新密码
          </label>
          <input
            type="password"
            value={password}
            onChange={handlePasswordChange}
            placeholder="请输入4位字母或数字"
            maxLength={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-center text-lg tracking-widest"
          />
        </div>

        {/* 确认密码输入 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            确认密码
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            placeholder="请再次输入4位密码"
            maxLength={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-center text-lg tracking-widest"
          />
          {confirmPassword.length >= 4 && password.length >= 4 && (
            <p className={`mt-1 text-xs ${
              passwordsMatch ? 'text-green-500' : 'text-red-500'
            }`}>
              {passwordsMatch ? '密码一致' : '密码不一致'}
            </p>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* 加载状态 */}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 text-blue-500 mb-4">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm">设置中...</span>
          </div>
        )}

        {/* 按钮 */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !passwordsMatch}
            className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? '设置中...' : '确定'}
          </button>
        </div>

        {/* 提示 */}
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          密码仅用于解锁笔记查看，不会加密笔记内容
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
